import {Resource, RESOURCE_TYPE_KTM, ResourceAccessRole, ResourcesFiltered, ResourceType} from "@/fyneai-shared/types";
import React, {ReactElement, useContext} from "react";
import {Button, SvgIcon, Typography, useMediaQuery, useTheme} from "@mui/material";
import {AddOutlined, ArticleOutlined, ExpandMoreOutlined} from "@mui/icons-material";
import {
  DialogFragment,
  PageFragment,
  PageFragmentProps,
  PageFragmentState,
  PageFragmentStyleFlags
} from "@/shared/PageFragment";
import {PAGE_FRAGMENT_WIDTH, PD_LG, PD_SM, PD_XXSM, SZ_JUMBO} from "@/shared/dimens";
import {ResourcesListView} from "./ResourcesListView";
import {BaseApp} from "@/shared/BaseApp";
import {StyledBoxColumn} from "@/shared/StyledComponents";
import {Action, ACTION_SPACE} from "@/shared/types";
import {ResourceContext} from "./contexts";
import {CreateProjectHelper} from "./CreateProjectHelper";

export type ResourceSelectFragmentProps = PageFragmentProps & {
  title?: string,
  resources: Resource[],
  selectedResource: Resource,
  setSelectedResource: (resource: Resource) => void,
  style?: any,
  createProject?: () => void,
}

type ResourceSelectFragmentState = PageFragmentState & {}

export class ResourceSelectFragment extends DialogFragment<ResourceSelectFragmentProps, ResourceSelectFragmentState> {

  protected onCreateState(props: ResourceSelectFragmentProps): ResourceSelectFragmentState {
    return {
      ...super.onCreateState(props),
      title: props.title,
    };
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx([
      ACTION_SPACE,
      Boolean(this.props.createProject)
        ? new Action("Create Project", () => {
          this.props.setSelectedResource(null);
          setTimeout(() => this.props.createProject(), 1);
        }, AddOutlined).setVariant("contained")
        : null,
    ].filter(Boolean));
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return Boolean(this.props.createProject) ? PageFragment.STYLE_TOOLBAR_TYPE_FLAG : 0;
  }

  renderContent(): ReactElement {
    return <StyledBoxColumn style={{...this.props.style, padding: PD_SM}}>
      <ResourcesListView
        resources={this.props.resources}
        selectedResource={this.props.selectedResource}
        setSelectedResource={selectedResource => this.props.setSelectedResource(selectedResource)}/>
    </StyledBoxColumn>
  }
}

export function ResourceSelect(props: {}): ReactElement {
  const resourceContext = useContext(ResourceContext);
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  const IconType: typeof SvgIcon = (resourceContext.selectedResource ? RESOURCE_TYPE_KTM.findMetadata(resourceContext.selectedResource.getType()).iconType : ArticleOutlined);
  return <Button
    startIcon={<IconType/>}
    endIcon={<ExpandMoreOutlined/>}
    style={{
      flexShrink: 0,
      minWidth: 0,
      maxWidth: SZ_JUMBO,
      minHeight: 40,
      padding: `${PD_XXSM}px ${PD_LG}px`
    }}
    onClick={() => {
      const id = BaseApp.CONTEXT.showDialog(null, () =>
        <ResourceSelectFragment
          title={"Select Resource"}
          style={{width: "84dvw", minHeight: 360, maxWidth: PAGE_FRAGMENT_WIDTH}}
          resources={ResourcesFiltered(resourceContext.resources, {excludeTypes: [ResourceType.FOLDER]})}
          selectedResource={resourceContext.selectedResource}
          setSelectedResource={(resource) => {
            BaseApp.CONTEXT.hideDialog(id);
            if (resource) {
              resourceContext.setSelectedResource(resource);
            }
          }}
          createProject={resourceContext.accessRole !== ResourceAccessRole.LABELER ? () => CreateProjectHelper.create() : null}
        />);
    }}>
    {md
      ? (resourceContext?.selectedResource
        ? <Typography variant="body2" noWrap={true}>{resourceContext.selectedResource.name}</Typography>
        : <Typography variant={"body2"}><i>Tap to select resource</i></Typography>)
      : null}
  </Button>
}